<svelte:head>
  <link rel="canonical" href={canonical}>
</svelte:head>
<Nav {page} />

<div class="main-content" ref:mainContent>
  <main class="{infiniteScrollPage ? 'infinite-scroll-page' : ''}">
    <svelte:component this={child.component} {...child.props} />
  </main>
  {#if !$isUserLoggedIn && page === 'home'}
  <InformationalFooter />
  {/if}
</div>
<style>
  /* this avoids a flash of the background color when switching timelines */
  .infinite-scroll-page {
    min-height: 100vh;
  }

  /* overflow-x: hidden would break position:sticky (ComposeStickyButton).
     Apply it only during the swipe animation via a JS-toggled class. */
  .main-content.swipe-animating {
    overflow-x: hidden;
  }

  @keyframes swipe-out-left {
    to { transform: translateX(-20%); opacity: 0; }
  }
  @keyframes swipe-out-right {
    to { transform: translateX(20%); opacity: 0; }
  }
  @keyframes swipe-in-from-right {
    from { transform: translateX(20%); opacity: 0; }
    to   { transform: translateX(0);   opacity: 1; }
  }
  @keyframes swipe-in-from-left {
    from { transform: translateX(-20%); opacity: 0; }
    to   { transform: translateX(0);    opacity: 1; }
  }

  :global(main.swipe-out-left)       { animation: swipe-out-left      80ms  cubic-bezier(0.4, 0, 1, 1)    forwards; }
  :global(main.swipe-out-right)      { animation: swipe-out-right     80ms  cubic-bezier(0.4, 0, 1, 1)    forwards; }
  :global(main.swipe-in-from-right)  { animation: swipe-in-from-right 120ms cubic-bezier(0, 0, 0.2, 1)    forwards; }
  :global(main.swipe-in-from-left)   { animation: swipe-in-from-left  120ms cubic-bezier(0, 0, 0.2, 1)    forwards; }

  @media (prefers-reduced-motion: reduce) {
    :global(main.swipe-out-left),
    :global(main.swipe-out-right),
    :global(main.swipe-in-from-right),
    :global(main.swipe-in-from-left) { animation: none; }
  }
</style>
<script>
  import { store } from './_store/store.js'
  import { observe } from 'svelte-extras'
  import { normalizePageName } from './_utils/normalizePageName.js'

  const SWIPE_MIN_X = 60   // minimum horizontal distance to count as a swipe
  const SWIPE_DIR_RATIO = 2 // horizontal must be at least 2× the vertical

  export default {
    components: {
      Nav: './_components/Nav.html',
      InformationalFooter: './_components/InformationalFooter.html'
    },
    oncreate () {
      if (ZOCIAL_IS_BROWSER && process.env.NODE_ENV !== 'production') {
        window.layoutObject = this
      }

      this.observe('page', page => {
        this.store.set({ currentPage: page })
      })

      this._swipeStartX = 0
      this._swipeStartY = 0

      this._onTouchStart = (e) => {
        this._swipeStartX = e.touches[0].clientX
        this._swipeStartY = e.touches[0].clientY
      }

      this._onTouchEnd = (e) => {
        const dx = e.changedTouches[0].clientX - this._swipeStartX
        const dy = e.changedTouches[0].clientY - this._swipeStartY
        if (Math.abs(dx) < SWIPE_MIN_X || Math.abs(dx) < Math.abs(dy) * SWIPE_DIR_RATIO) return

        const { navPages } = this.store.get()
        const { page } = this.get()
        const currentIdx = navPages.findIndex(p => p.name === normalizePageName(page))
        if (currentIdx === -1) return // not a top-level nav page

        const goLeft = dx < 0
        const targetIdx = goLeft ? currentIdx + 1 : currentIdx - 1
        if (targetIdx < 0 || targetIdx >= navPages.length) return // boundary

        const mainContent = this.refs.mainContent
        const main = mainContent.querySelector('main')

        // Clip horizontal overflow only for the duration of the animation so
        // that position:sticky (ComposeStickyButton FAB) keeps working normally.
        mainContent.classList.add('swipe-animating')

        // slide current content out
        main.classList.add(goLeft ? 'swipe-out-left' : 'swipe-out-right')

        setTimeout(() => {
          import('../../__sapper__/client.js').then(({ goto }) => goto(navPages[targetIdx].href))
          main.classList.remove('swipe-out-left', 'swipe-out-right')
          // slide new content in from the opposite side
          main.classList.add(goLeft ? 'swipe-in-from-right' : 'swipe-in-from-left')
          setTimeout(() => {
            main.classList.remove('swipe-in-from-right', 'swipe-in-from-left')
            mainContent.classList.remove('swipe-animating')
          }, 130)
        }, 80)
      }

      const el = this.refs.mainContent
      el.addEventListener('touchstart', this._onTouchStart, { passive: true })
      el.addEventListener('touchend', this._onTouchEnd, { passive: true })
    },
    ondestroy () {
      const el = this.refs.mainContent
      if (el) {
        el.removeEventListener('touchstart', this._onTouchStart)
        el.removeEventListener('touchend', this._onTouchEnd)
      }
    },
    store: () => store,
    computed: {
      page: ({ child }) => child.props.path.slice(1) || 'home',
      infiniteScrollPage: ({ $isUserLoggedIn, page }) => $isUserLoggedIn && !page.startsWith('settings'),
      canonical: ({ path }) => (ZOCIAL_IS_BROWSER ? location.origin + path : process.env.UPSTREAM ? 'https://zocial.social' + path : null)
    },
    methods: {
      observe
    }
  }
</script>
